import { useState, useEffect, useCallback } from 'react';
import { RepoData, CommitStatus } from '@app-types/types';
import {
  getRepoData,
  setRepo,
  fetchOpenPrs,
  removePr,
  getPrStatus,
  rerunE2e,
  toggleAutoRerun,
} from '@services/githubRepos';

const GitHubRepos: React.FC = () => {
  const [data, setData] = useState<RepoData>({ repo: null, prs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Repo config form
  const [repoName, setRepoName] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [savingRepo, setSavingRepo] = useState(false);
  const [editingRepo, setEditingRepo] = useState(false);

  // Fetch PRs by author
  const [authorInput, setAuthorInput] = useState('');
  const [fetchingPrs, setFetchingPrs] = useState(false);

  // Per-PR state
  const [commitStatuses, setCommitStatuses] = useState<Record<string, CommitStatus>>({});
  const [statusLoading, setStatusLoading] = useState<Record<string, boolean>>({});
  const [rerunning, setRerunning] = useState<Record<string, boolean>>({});
  const [expandedPrs, setExpandedPrs] = useState<Record<string, boolean>>({});

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const result = await getRepoData();
      setData(result);
      if (result.repo) {
        setRepoName(result.repo.name);
        setRepoUrl(result.repo.url);
      }
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveRepo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!repoName.trim() || !repoUrl.trim()) return;
    setSavingRepo(true);
    setError(null);
    try {
      const result = await setRepo(repoName.trim(), repoUrl.trim());
      setData(result);
      setEditingRepo(false);
    } catch {
      setError('Failed to save repository');
    } finally {
      setSavingRepo(false);
    }
  };

  const handleFetchPrs = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authorInput.trim()) return;
    setFetchingPrs(true);
    setError(null);
    setCommitStatuses({});
    try {
      const result = await fetchOpenPrs(authorInput.trim());
      setData(result);
    } catch {
      setError('Failed to fetch open PRs');
    } finally {
      setFetchingPrs(false);
    }
  };

  const handleRemovePr = async (prId: string) => {
    setError(null);
    try {
      await removePr(prId);
      setData((prev) => ({ ...prev, prs: prev.prs.filter((p) => p.id !== prId) }));
    } catch {
      setError('Failed to remove PR');
    }
  };

  const fetchPrStatus = async (prId: string) => {
    setStatusLoading((prev) => ({ ...prev, [prId]: true }));
    try {
      const status = await getPrStatus(prId);
      setCommitStatuses((prev) => ({ ...prev, [prId]: status }));
    } catch {
      setError('Failed to fetch status');
    } finally {
      setStatusLoading((prev) => ({ ...prev, [prId]: false }));
    }
  };

  const handleRerunE2e = async (prId: string) => {
    setRerunning((prev) => ({ ...prev, [prId]: true }));
    setError(null);
    try {
      await rerunE2e(prId);
      setTimeout(() => fetchPrStatus(prId), 2000);
    } catch {
      setError('Failed to rerun E2E');
    } finally {
      setRerunning((prev) => ({ ...prev, [prId]: false }));
    }
  };

  const handleToggleAutoRerun = async (prId: string) => {
    setError(null);
    try {
      const result = await toggleAutoRerun(prId);
      setData(result);
    } catch {
      setError('Failed to toggle auto-rerun');
    }
  };

  const toggleExpanded = (prId: string) => {
    setExpandedPrs((prev) => ({ ...prev, [prId]: !prev[prId] }));
  };

  // Auto-fetch status for PRs that don't have it yet
  useEffect(() => {
    (data.prs || []).forEach((pr) => {
      if (!commitStatuses[pr.id] && !statusLoading[pr.id]) {
        fetchPrStatus(pr.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.prs]);

  const stateColor = (state: string) => {
    switch (state) {
      case 'success': return '#16a34a';
      case 'pending': return '#d97706';
      case 'failure':
      case 'error': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const stateBg = (state: string) => {
    switch (state) {
      case 'success': return '#f0fdf4';
      case 'pending': return '#fffbeb';
      case 'failure':
      case 'error': return '#fef2f2';
      default: return '#f9fafb';
    }
  };

  const stateBorder = (state: string) => {
    switch (state) {
      case 'success': return '#bbf7d0';
      case 'pending': return '#fde68a';
      case 'failure':
      case 'error': return '#fecaca';
      default: return '#e5e7eb';
    }
  };

  const stateLabel = (state: string) => {
    switch (state) {
      case 'success': return 'Passed';
      case 'pending': return 'Pending';
      case 'failure': return 'Failed';
      case 'error': return 'Error';
      default: return state;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>Loading...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '0.625rem 1rem',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.625rem',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#dc2626', fontSize: '0.8125rem', fontWeight: 500 }}>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 0.25rem' }}
          >
            ×
          </button>
        </div>
      )}

      {/* Single unified SaaS card */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        {/* Card title bar */}
        <div
          style={{
            padding: '0.625rem 1.25rem',
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Open SaaS PRs
          </span>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}>
            {data.repo && !editingRepo && (
              <form onSubmit={handleFetchPrs} style={{ display: 'flex', flexDirection: 'row', gap: '0.375rem', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="GitHub author"
                  value={authorInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAuthorInput(e.target.value)}
                  style={{
                    padding: '0.3125rem 0.625rem',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    width: '140px',
                    outline: 'none',
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: 'white',
                    boxSizing: 'border-box' as const,
                  }}
                  required
                />
                <button
                  type="submit"
                  disabled={fetchingPrs}
                  style={{
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '0.375rem',
                    padding: '0.3125rem 0.625rem',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    opacity: fetchingPrs ? 0.5 : 1,
                    whiteSpace: 'nowrap' as const,
                  }}
                >
                  {fetchingPrs ? '...' : 'Fetch PRs'}
                </button>
              </form>
            )}
            {data.repo && !editingRepo && (
              <button
                type="button"
                onClick={() => setEditingRepo(true)}
                style={{
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: '0.375rem',
                  padding: '0.1875rem 0.5rem',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  textTransform: 'uppercase' as const,
                  letterSpacing: '0.04em',
                }}
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Repo config: edit form or display */}
        {!data.repo || editingRepo ? (
          <form onSubmit={handleSaveRepo} style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. SaaS"
                  value={repoName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepoName(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.8125rem',
                    width: '100%',
                    boxSizing: 'border-box' as const,
                    outline: 'none',
                    backgroundColor: '#f9fafb',
                    transition: 'border-color 0.15s',
                  }}
                  required
                />
              </div>
              <div style={{ flex: 3, minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  URL
                </label>
                <input
                  type="url"
                  placeholder="https://github.com/owner/repo"
                  value={repoUrl}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepoUrl(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    fontSize: '0.8125rem',
                    width: '100%',
                    boxSizing: 'border-box' as const,
                    outline: 'none',
                    backgroundColor: '#f9fafb',
                    transition: 'border-color 0.15s',
                  }}
                  required
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={savingRepo}
                  style={{
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 1.25rem',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    opacity: savingRepo ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}
                >
                  {savingRepo ? 'Saving...' : 'Save'}
                </button>
                {editingRepo && (
                  <button
                    type="button"
                    onClick={() => { setEditingRepo(false); setRepoName(data.repo!.name); setRepoUrl(data.repo!.url); }}
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 1rem',
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      color: '#374151',
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </form>
        ) : (
          <>
            {/* Repo info row */}
            <div style={{ padding: '0.625rem 1.25rem', display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.875rem' }}>
                {data.repo.name}
              </span>
              <span style={{ color: '#d1d5db' }}>·</span>
              <a
                href={data.repo.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#6b7280', fontSize: '0.75rem', textDecoration: 'none', fontFamily: "'SF Mono', 'Fira Code', monospace" }}
              >
                {data.repo.url.replace(/^https?:\/\/(www\.)?github\.com\//, '')}
              </a>
            </div>

            {/* PR list (inline, same card) */}
            {data.prs.length === 0 ? (
              <div style={{ padding: '2.5rem 1rem', textAlign: 'center' }}>
                <div style={{ color: '#d1d5db', fontSize: '1.5rem', marginBottom: '0.5rem' }}>📋</div>
                <span style={{ color: '#9ca3af', fontSize: '0.8125rem' }}>
                  No PRs tracked — enter an author above to fetch open PRs
                </span>
              </div>
            ) : (
              <div>
                {data.prs.map((pr, idx) => {
                  const status = commitStatuses[pr.id];
                  const isLoading = statusLoading[pr.id];
                  const isRerunning = rerunning[pr.id];
                  const isExpanded = expandedPrs[pr.id];
                  const hasChecks = status && status.statuses.length > 0;
                  const hasE2e = status?.statuses.some((s) => s.context === 'e2e tests');
                  const e2eNotStarted = status && !hasE2e;
                  const prState = status?.state || '';

                  return (
                    <div
                      key={pr.id}
                      style={{
                        borderTop: '1px solid #f3f4f6',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          borderLeft: `3px solid ${status ? stateColor(prState) : '#e5e7eb'}`,
                          cursor: hasChecks ? 'pointer' : 'default',
                          transition: 'background-color 0.1s',
                        }}
                        onClick={() => hasChecks && toggleExpanded(pr.id)}
                      >
                        {/* Left section: chevron + PR number + badges */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.625rem 0 0.625rem 0.875rem',
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          {/* Expand chevron */}
                          <span
                            style={{
                              fontSize: '0.5rem',
                              color: hasChecks ? '#9ca3af' : 'transparent',
                              width: '0.625rem',
                              flexShrink: 0,
                              textAlign: 'center',
                              userSelect: 'none',
                              transition: 'transform 0.15s',
                              display: 'inline-block',
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                          >
                            ▶
                          </span>

                          {/* PR number */}
                          <span style={{ fontWeight: 700, color: '#111827', fontSize: '0.8125rem', fontFamily: "'SF Mono', 'Fira Code', monospace", flexShrink: 0 }}>
                            #{pr.prNumber}
                          </span>

                          {/* Status badge */}
                          {status ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                backgroundColor: stateBg(prState),
                                border: `1px solid ${stateBorder(prState)}`,
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  width: '0.375rem',
                                  height: '0.375rem',
                                  borderRadius: '50%',
                                  backgroundColor: stateColor(prState),
                                  display: 'inline-block',
                                }}
                              />
                              <span style={{ fontSize: '0.625rem', fontWeight: 600, color: stateColor(prState) }}>
                                {stateLabel(prState)}
                              </span>
                            </span>
                          ) : isLoading ? (
                            <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>loading...</span>
                          ) : null}

                          {/* E2E not started indicator */}
                          {e2eNotStarted && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                backgroundColor: '#f3f4f6',
                                border: '1px solid #e5e7eb',
                                flexShrink: 0,
                              }}
                            >
                              <span
                                style={{
                                  width: '0.375rem',
                                  height: '0.375rem',
                                  borderRadius: '50%',
                                  backgroundColor: '#9ca3af',
                                  display: 'inline-block',
                                }}
                              />
                              <span style={{ fontSize: '0.625rem', fontWeight: 500, color: '#6b7280' }}>
                                E2E not started
                              </span>
                            </span>
                          )}

                          {/* Check count */}
                          {hasChecks && !isExpanded && (
                            <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>
                              {status.statuses.length} check{status.statuses.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Right section: actions */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '0.25rem',
                            alignItems: 'center',
                            padding: '0.625rem 1rem 0.625rem 0.5rem',
                            flexShrink: 0,
                          }}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          {/* Auto-rerun toggle */}
                          <label
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              cursor: 'pointer',
                              userSelect: 'none',
                              padding: '0.1875rem 0.5rem',
                              borderRadius: '9999px',
                              backgroundColor: pr.autoRerun ? '#eff6ff' : '#f9fafb',
                              border: pr.autoRerun ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
                              transition: 'all 0.15s',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!pr.autoRerun}
                              onChange={() => handleToggleAutoRerun(pr.id)}
                              style={{ cursor: 'pointer', accentColor: '#2563eb', width: '0.75rem', height: '0.75rem', margin: 0 }}
                            />
                            <span style={{ fontSize: '0.625rem', color: pr.autoRerun ? '#2563eb' : '#9ca3af', fontWeight: 600 }}>
                              Auto
                            </span>
                          </label>

                          <button
                            type="button"
                            onClick={() => fetchPrStatus(pr.id)}
                            disabled={isLoading}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: '0.375rem',
                              padding: '0.25rem 0.375rem',
                              fontSize: '0.6875rem',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              color: '#6b7280',
                              opacity: isLoading ? 0.4 : 1,
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                            title="Refresh status"
                          >
                            ↻
                          </button>

                          {(e2eNotStarted || (status && status.state !== 'success')) && (
                            <button
                              type="button"
                              onClick={() => handleRerunE2e(pr.id)}
                              disabled={isRerunning}
                              style={{
                                border: '1px solid #fde68a',
                                borderRadius: '0.375rem',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.625rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                backgroundColor: '#fffbeb',
                                color: '#92400e',
                                opacity: isRerunning ? 0.4 : 1,
                                lineHeight: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap' as const,
                              }}
                              title="Rerun E2E tests"
                            >
                              {isRerunning ? '...' : 'Rerun E2E'}
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleRemovePr(pr.id)}
                            style={{
                              border: '1px solid #fecaca',
                              borderRadius: '0.375rem',
                              padding: '0.25rem 0.375rem',
                              fontSize: '0.6875rem',
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              color: '#ef4444',
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                            }}
                            title="Remove PR"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {/* Expanded status checks */}
                      {isExpanded && hasChecks && (
                        <div
                          style={{
                            backgroundColor: '#f8fafc',
                            borderTop: '1px solid #f1f5f9',
                            maxHeight: '240px',
                            overflowY: 'auto',
                          }}
                        >
                          {status.statuses.map((s, i) => (
                            <div
                              key={i}
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.375rem 1.25rem 0.375rem 2.5rem',
                                borderBottom: i < status.statuses.length - 1 ? '1px solid #f1f5f9' : 'none',
                              }}
                            >
                              <span
                                style={{
                                  width: '0.375rem',
                                  height: '0.375rem',
                                  borderRadius: '50%',
                                  backgroundColor: stateColor(s.state),
                                  flexShrink: 0,
                                  display: 'inline-block',
                                }}
                              />
                              <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.6875rem', flexShrink: 0, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.context}
                              </span>
                              <span style={{ color: '#9ca3af', fontSize: '0.6875rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.description}
                              </span>
                              {s.target_url && (
                                <a
                                  href={s.target_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#3b82f6', fontSize: '0.6875rem', textDecoration: 'none', flexShrink: 0, fontWeight: 600 }}
                                >
                                  View →
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GitHubRepos;
